/**
 * Prompt-config guardrails — webapp mirror of
 * `infrastructure/lambdas/admin/prompt_validation.py`. The server is
 * AUTHORITATIVE; this exists so an admin sees the problem before a round trip.
 * Keep the two in sync (same enums, same thresholds, same messages).
 *
 * The three failure modes being guarded:
 *  1. traffic_pct is a 0-1 FRACTION in DynamoDB (prompt_resolver compares
 *     `user_pct <= traffic_pct`). The editor shows a percentage and converts on
 *     change, so a stored value > 1 is legacy data or raw-API misuse.
 *  2. validate/fix/classify prompts judge / repair / label existing content —
 *     a creative temperature makes their verdicts non-deterministic.
 *  3. `prompt_text` is the CACHED static block; a per-call token in it
 *     (`{{NONCE}}`, or the `{{GET_UNIQUE_RESPONSE}}` snippet that contains one)
 *     guarantees a 100% Bedrock prompt-cache miss.
 */

export const PROMPT_TYPES = [
  'generate', 'validate', 'fix', 'classify', 'snippet', 'import', 'embedding', 'uat',
] as const;

export const PROMPT_STATES = ['prod', 'test', 'expired'] as const;

export const LOW_TEMPERATURE_TYPES = ['validate', 'fix', 'classify'] as const;
export const LOW_TEMPERATURE_MAX = 0.4;

export const CACHE_BUSTING_TOKENS = ['NONCE', 'GET_UNIQUE_RESPONSE'];

const TOKEN_RE = /\{\{([A-Z][A-Z0-9_]*)\}\}/g;
const CATCH_ALL_VALUES = ['', 'catch-all'];

export interface PromptPayload {
  prompt_type?: string;
  prompt_state?: string;
  traffic_pct?: string | number | null;
  traffic_offset?: string | number | null;
  temperature?: string | number | null;
  prompt_text?: string;
  prompt_text_variable?: string;
  allow_high_temperature?: boolean;
  [key: string]: unknown;
}

export interface PromptValidationResult {
  error: string | null;
  warnings: string[];
}

export function isCatchAll(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return CATCH_ALL_VALUES.includes(value.trim().toLowerCase());
  return false;
}

/** Parse to a finite number, or null when it isn't one. Booleans are not numbers. */
function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return null;
    const num = Number(text);
    return Number.isFinite(num) ? num : null;
  }
  return null;
}

/** Render a number without float noise (0.5 -> "0.5", 1 -> "1"). */
function fmt(value: number): string {
  return String(Math.round(value * 1e6) / 1e6);
}

export function validateTrafficPct(value: unknown): string | null {
  if (isCatchAll(value)) return null;
  const num = toNumber(value);
  if (num === null) {
    return `traffic_pct must be a number between 0 and 1, '' or 'catch-all' (got ${JSON.stringify(value)})`;
  }
  if (num < 0) {
    return `traffic_pct must be >= 0 (got ${fmt(num)}). Use 0 to disable this version.`;
  }
  if (num > 1) {
    return `traffic_pct is a 0-1 fraction; ${fmt(num)}% must be sent as ${fmt(num / 100)} (1 = 100% of traffic)`;
  }
  return null;
}

export function validateTrafficOffset(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const num = toNumber(value);
  if (num === null) {
    return `traffic_offset must be a number between 0 and 1 (got ${JSON.stringify(value)})`;
  }
  if (num < 0 || num > 1) {
    return `traffic_offset must be between 0 and 1 (got ${fmt(num)}) — it is a hash rotation seed, not a percentage`;
  }
  return null;
}

export function validateTemperature(
  value: unknown,
  promptType?: string,
  allowHighTemperature = false,
): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const num = toNumber(value);
  if (num === null) {
    return `temperature must be a number between 0 and 1 (got ${JSON.stringify(value)})`;
  }
  if (num < 0 || num > 1) {
    return `temperature must be between 0 and 1 (got ${fmt(num)})`;
  }
  if (
    promptType && (LOW_TEMPERATURE_TYPES as readonly string[]).includes(promptType)
    && num > LOW_TEMPERATURE_MAX && !allowHighTemperature
  ) {
    return `temperature ${fmt(num)} is too high for a '${promptType}' prompt. `
      + `${LOW_TEMPERATURE_TYPES.join('/')} prompts are factual calls (they judge, repair or `
      + `label existing content) and must be near-deterministic — keep temperature <= ${LOW_TEMPERATURE_MAX}.`;
  }
  return null;
}

/** Short hint rendered under the temperature input. */
export function temperaturePolicyHint(promptType?: string): string {
  if (promptType && (LOW_TEMPERATURE_TYPES as readonly string[]).includes(promptType)) {
    return `${promptType} prompts are factual — keep ≤ ${LOW_TEMPERATURE_MAX} for repeatable results`;
  }
  if (promptType === 'snippet') return 'snippets have no temperature of their own';
  if (promptType === 'embedding') return 'embedding calls ignore temperature';
  return '0 = deterministic, 1 = most creative';
}

/** Distinct {{TOKEN}} placeholders, in first-seen order. */
export function extractTokens(text: string): string[] {
  const seen: string[] = [];
  for (const match of text.matchAll(TOKEN_RE)) {
    if (!seen.includes(match[1])) seen.push(match[1]);
  }
  return seen;
}

export function validateStaticPromptText(promptText: unknown): PromptValidationResult {
  const warnings: string[] = [];
  if (!promptText || typeof promptText !== 'string') return { error: null, warnings };

  const tokens = extractTokens(promptText);
  const busting = tokens.filter((t) => CACHE_BUSTING_TOKENS.includes(t));
  if (busting.length) {
    return {
      error: `prompt_text (static) contains ${busting.map((t) => `{{${t}}}`).join(', ')}`
        + ' — these change on EVERY call, so the static block never matches the Bedrock'
        + ' prompt cache (100% cache miss, full input cost every time). Move them to'
        + ' prompt_text_variable (the non-cached block).',
      warnings,
    };
  }

  const other = tokens.filter((t) => !CACHE_BUSTING_TOKENS.includes(t));
  if (other.length) {
    warnings.push(
      `prompt_text (static, cached) contains placeholder(s) ${other.map((t) => `{{${t}}}`).join(', ')}.`
      + ' Snippet names are fine (they expand before the cache key is built), but any RUNTIME'
      + ' parameter here changes the cached block per call and defeats prompt caching — those'
      + ' belong in prompt_text_variable.',
    );
  }
  return { error: null, warnings };
}

/**
 * Full pre-flight check for a prompt save. Mirrors
 * admin/prompt_validation.validate_prompt_payload.
 */
export function validatePromptPayload(
  payload: PromptPayload,
  effectivePromptType?: string,
): PromptValidationResult {
  const warnings: string[] = [];
  const allowHigh = Boolean(payload.allow_high_temperature);
  const has = (key: string) => Object.prototype.hasOwnProperty.call(payload, key)
    && payload[key] !== undefined;

  const promptType = has('prompt_type') ? payload.prompt_type : effectivePromptType;

  if (has('prompt_type')) {
    if (!(PROMPT_TYPES as readonly string[]).includes(String(payload.prompt_type))) {
      return {
        error: `prompt_type must be one of ${PROMPT_TYPES.join(', ')} (got ${JSON.stringify(payload.prompt_type)})`,
        warnings,
      };
    }
  }

  if (has('prompt_state')) {
    if (!(PROMPT_STATES as readonly string[]).includes(String(payload.prompt_state))) {
      return {
        error: `prompt_state must be one of ${PROMPT_STATES.join(', ')} (got ${JSON.stringify(payload.prompt_state)}).`
          + " Only 'prod' versions serve real users; 'test' serves test users; 'expired' is retired.",
        warnings,
      };
    }
  }

  if (has('traffic_pct')) {
    const error = validateTrafficPct(payload.traffic_pct);
    if (error) return { error, warnings };
  }

  if (has('traffic_offset')) {
    const error = validateTrafficOffset(payload.traffic_offset);
    if (error) return { error, warnings };
  }

  if (has('temperature')) {
    const error = validateTemperature(payload.temperature, promptType, allowHigh);
    if (error) return { error, warnings };
  }

  if (has('prompt_text')) {
    const result = validateStaticPromptText(payload.prompt_text);
    if (result.error) return { error: result.error, warnings };
    warnings.push(...result.warnings);
  }

  return { error: null, warnings };
}

/**
 * Render a stored traffic_pct for the version list.
 * `1` -> "100%", `0.1` -> "10%", '' / 'catch-all' / absent -> "catch-all",
 * `0` -> "off", anything > 1 -> flagged as legacy (needs migration).
 */
export function formatTrafficPct(value: unknown): string {
  if (isCatchAll(value)) return 'catch-all';
  const num = toNumber(value);
  if (num === null) return `legacy: needs migration (${String(value)})`;
  if (num > 1) return `${fmt(num)} — legacy: needs migration`;
  if (num <= 0) return 'off';
  return `${fmt(Math.round(num * 1000) / 10)}%`;
}
